/*
 * This feature watches the shopper's selected product choice.
 * If that choice is sold out, or QA visibility is forced on, it loads one replacement product.
 */
class TryThisInstead extends HTMLElement {
  /*
   * Set QAQC and request tracking before the feature starts running.
   */
  constructor() {
    super();
    this.forceVisible = false;
    this.forceFirstRecommendationUnavailable = false;
    this.requestId = 0;
    this.onVariantChange = this.handleVariantChange.bind(this);
  }

  /*
   * Set up the feature when it appears on the page.
   */
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productFormInput = document.querySelector(
      `#product-form-${this.sectionId} input[name="id"]`
    );
    this.content = this.querySelector('.try-this-instead__content');
    this.variants = this.getVariants();

    if (this.productFormInput) {
      this.productFormInput.addEventListener('change', this.onVariantChange);
    }

    this.handleVariantChange();
  }

  /*
   * Clean up the event listener when the feature is removed from the page.
   */
  disconnectedCallback() {
    if (this.productFormInput && this.onVariantChange) {
      this.productFormInput.removeEventListener('change', this.onVariantChange);
    }
  }

  /*
   * Read the product choice data Shopify already printed into the page.
   */
  getVariants() {
    const variantJson = document.querySelector(
      `variant-radios[data-section="${this.sectionId}"] script[type="application/json"], variant-selects[data-section="${this.sectionId}"] script[type="application/json"]`
    );

    if (!variantJson) return [];

    try {
      return JSON.parse(variantJson.textContent);
    } catch (_error) {
      return [];
    }
  }

  /*
   * Get the currently selected variant ID.
   */
  getCurrentVariantId() {
    return String(this.productFormInput?.value || this.dataset.initialVariantId || '');
  }

  /*
   * Check whether the shopper's current product choice is in stock.
   */
  isCurrentVariantAvailable() {
    const variantId = this.getCurrentVariantId();
    const currentVariant = this.variants.find((variant) => String(variant.id) === variantId);

    if (currentVariant) {
      return currentVariant.available;
    }

    return this.dataset.initialVariantAvailable === 'true';
  }

  /*
   * Decide whether the replacement products should be treated as active.
   */
  shouldShowRecommendations() {
    return this.forceVisible || !this.isCurrentVariantAvailable();
  }

  /*
   * Re-check stock whenever the page loads or the shopper changes options.
   */
  handleVariantChange() {
    const variantId = this.getCurrentVariantId();

    if (!this.shouldShowRecommendations() || !variantId) {
      this.updateVisibility();
      return;
    }

    this.loadRecommendations(variantId);
  }

  /*
   * Show the section only when a replacement card is ready to display
   * and the sold-out or QA visibility rules say it should be visible.
   */
  updateVisibility() {
    const shouldShow = this.shouldShowRecommendations() && Boolean(this.content?.childElementCount);
    this.classList.toggle('hidden', !shouldShow);
  }

  /*
   * Fetch the replacement handles and render the first in-stock card.
   */
  async loadRecommendations(variantId) {
    const requestId = ++this.requestId;

    if (this.content) {
      this.content.innerHTML = '';
    }

    this.updateVisibility();

    try {
      const handles = await this.fetchHandles(variantId);

      if (
        requestId !== this.requestId ||
        !handles.length ||
        this.getCurrentVariantId() !== variantId ||
        !this.shouldShowRecommendations()
      ) {
        return;
      }

      let cardMarkup = '';

      for (const [index, handle] of handles.entries()) {
        if (index === 0 && this.forceFirstRecommendationUnavailable) {
          continue;
        }

        cardMarkup = await this.fetchCard(handle);

        if (cardMarkup) {
          break;
        }
      }

      if (
        requestId !== this.requestId ||
        this.getCurrentVariantId() !== variantId ||
        !this.shouldShowRecommendations()
      ) {
        return;
      }

      if (cardMarkup && this.content) {
        this.content.innerHTML = cardMarkup;
      }
    } catch (_error) {} finally {
      if (requestId === this.requestId) {
        this.updateVisibility();
      }
    }
  }

  /*
   * Ask Rebuy's custom endpoint for up to two handles,
   * so the second product can act as an inventory fallback.
   */
  async fetchHandles(variantId) {
    const params = new URLSearchParams({
      key: '41bafb2eca542653dda5b8e01ceb35be41060ac5',
      shopify_product_ids: String(this.dataset.productId || ''),
      shopify_variant_ids: String(variantId || ''),
      format: 'pretty',
      limit: '2',
    });

    const response = await fetch(
      `https://rebuyengine.com/api/v1/custom/id/286267?${params.toString()}`
    );

    if (!response.ok) {
      return [];
    }

    const json = await response.json();
    const uniqueHandles = new Set();

    return (json?.data || [])
      .filter((item) => item?.handle && String(item?.id) !== String(this.dataset.productId))
      .map((item) => item.handle)
      .filter((handle) => {
        if (uniqueHandles.has(handle)) {
          return false;
        }

        uniqueHandles.add(handle);
        return true;
      });
  }

  /*
   * Load the ready-made card markup for one replacement product.
   */
  async fetchCard(handle) {
    const response = await fetch(`/products/${handle}?view=try-this-instead-card`);

    if (!response.ok) {
      return '';
    }

    const markup = (await response.text()).trim();

    if (!markup || !markup.includes('try-this-instead-card')) {
      return '';
    }

    return markup;
  }
}

/*
 * Connect the page name `try-this-instead` to the code above.
 */
if (!customElements.get('try-this-instead')) {
  customElements.define('try-this-instead', TryThisInstead);
}
